import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Col, Dropdown, Menu, Row } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAdd } from '@fortawesome/pro-solid-svg-icons/faAdd'
import { faList } from '@fortawesome/pro-solid-svg-icons/faList'
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons/faEllipsisV'
import { faGridHorizontal } from '@fortawesome/pro-solid-svg-icons/faGridHorizontal'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import EmptyState from 'components/reusables/emptyState'
import {
  DragAndDrop,
  Options,
  Question,
  TableBody,
  TableHeader,
} from 'components/administration/components/blocks/components/block-type/components/questionBank/stages/questions/sections/list/section/questions/styled'
import { AddButton, Count, Panel, Toolbar, ToolbarActions } from './styled'

const PREFIX = 'administration.surveyEditor.list'

export interface SurveyQuestionItem {
  key: number
  name: string
  typeLabel: string
  mandatory: boolean
}

interface IProps {
  items: SurveyQuestionItem[]
  onAdd: () => void
  onEdit: (key: number) => void
  onDelete: (key: number) => void
  /** When set, rows can be reordered. The course block has no order endpoint. */
  onReorder?: (from: number, to: number) => void
  rowTestId?: (index: number) => string
  toolbar?: ReactNode
}

/** Question table of the question bank, with type and mandatory instead of score. */
export const SurveyQuestionRows = ({
  items,
  onAdd,
  onEdit,
  onDelete,
  onReorder,
  rowTestId,
  toolbar,
}: IProps) => {
  const { t } = useTranslation()

  const header = (
    <TableHeader>
      <Row type="flex">
        {onReorder && <Col style={{ width: 40 }} />}
        <Col style={{ flex: 1 }}>{t(`${PREFIX}.columns.question`)}</Col>
        <Col style={{ width: 180 }}>{t(`${PREFIX}.columns.type`)}</Col>
        <Col style={{ width: 140 }}>{t(`${PREFIX}.columns.mandatory`)}</Col>
        <Col style={{ width: 32 }} />
      </Row>
    </TableHeader>
  )

  const menu = (key: number) => (
    <Menu>
      <Menu.Item
        data-testid={`survey-question-edit-${key}`}
        onClick={() => onEdit(key)}
      >
        {t(`${PREFIX}.edit`)}
      </Menu.Item>
      <Menu.Item
        data-testid={`survey-question-remove-${key}`}
        onClick={() => onDelete(key)}
      >
        {t(`${PREFIX}.remove`)}
      </Menu.Item>
    </Menu>
  )

  const row = (item: SurveyQuestionItem, index: number, handle?: object) => (
    <Row
      type="flex"
      style={{ backgroundColor: 'white' }}
      data-testid={rowTestId?.(index)}
    >
      {onReorder && (
        <Col style={{ width: 40 }}>
          <DragAndDrop>
            <Button type="link" {...handle}>
              <FontAwesomeIcon icon={faGridHorizontal} />
            </Button>
          </DragAndDrop>
        </Col>
      )}
      <Col style={{ flex: 1, display: 'grid' }}>
        <Question>{item.name}</Question>
      </Col>
      <Col style={{ width: 180 }}>{item.typeLabel}</Col>
      <Col style={{ width: 140 }}>
        {item.mandatory ? t(`${PREFIX}.yes`) : ''}
      </Col>
      <Col style={{ width: 32 }}>
        <Dropdown overlay={menu(item.key)} trigger={['click']}>
          <Options>
            <Button data-testid={`survey-question-options-${item.key}`}>
              <FontAwesomeIcon icon={faEllipsisV} />
            </Button>
          </Options>
        </Dropdown>
      </Col>
    </Row>
  )

  const rows = onReorder ? (
    <DragDropContext
      onDragEnd={(result) => {
        const { destination, source } = result
        if (!destination || destination.index === source.index) return
        onReorder(source.index, destination.index)
      }}
    >
      <Droppable droppableId="survey-questions">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items.map((item, index) => (
              <Draggable
                key={item.key}
                draggableId={String(item.key)}
                index={index}
              >
                {(itemProvided) => (
                  <div
                    ref={itemProvided.innerRef}
                    {...itemProvided.draggableProps}
                    style={{ ...itemProvided.draggableProps.style }}
                  >
                    {row(item, index, itemProvided.dragHandleProps)}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  ) : (
    items.map((item, index) => <div key={item.key}>{row(item, index)}</div>)
  )

  return (
    <Panel>
      {items.length === 0 ? (
        <EmptyState
          title={t(`${PREFIX}.empty`)}
          icon={faList}
          iconSize="small"
          action={onAdd}
          actionIcon={faAdd}
          actionTitle={t(`${PREFIX}.add`)}
        />
      ) : (
        <>
          <Toolbar>
            <Count>{t(`${PREFIX}.rowsCount`, { count: items.length })}</Count>
            <ToolbarActions>
              {toolbar}
              <AddButton type="button" onClick={onAdd}>
                <FontAwesomeIcon icon={faAdd} />
                {t(`${PREFIX}.add`)}
              </AddButton>
            </ToolbarActions>
          </Toolbar>
          {header}
          <TableBody>{rows}</TableBody>
        </>
      )}
      {items.length === 0 && toolbar && (
        <Toolbar>
          <ToolbarActions>{toolbar}</ToolbarActions>
        </Toolbar>
      )}
    </Panel>
  )
}
